import { captureException } from "@sentry/nextjs"
import axios, { AxiosResponse } from "axios"

const urlPath = process.env.NEXT_PUBLIC_GOOGLE_REDIS_ENDPOINT || ""

export interface PlaceAutocompleteResult {
  description: string
  distance_meters?: number
  place_id: string
  terms: {
    offset: number
    value: string
  }[]
  types: string[]
  matched_substrings: {
    length: number
    offset: number
  }[]
  structured_formatting: {
    main_text: string
    main_text_matched_substrings: {
      offset: number
      length: number
    }[]
    secondary_text: string
    secondary_text_matched_substrings: {
      offset: number
      length: number
    }[]
  }
}
interface PlaceAutocompleteResponseData extends ResponseData {
  predictions: PlaceAutocompleteResult[]
}
export interface PlaceAutocompleteResponse extends AxiosResponse {
  data: PlaceAutocompleteResponseData
}

export async function getAddressList(inputValue: string) {
  try {
    const address = encodeURIComponent(inputValue)
    const response = await axios.get(`${urlPath}/autoComplete/${address}`)

    return response.data as PlaceAutocompleteResponse
  } catch (err) {
    console.error(err)
    captureException(
      new Error(`Error fetching autocomplete ${JSON.stringify(err)}`),
    )
  }
}

export enum Status {
  OK = "OK",
  INVALID_REQUEST = "INVALID_REQUEST",
  MAX_WAYPOINTS_EXCEEDED = "MAX_WAYPOINTS_EXCEEDED",
  MAX_ROUTE_LENGTH_EXCEEDED = "MAX_ROUTE_LENGTH_EXCEEDED",
  OVER_DAILY_LIMIT = "OVER_DAILY_LIMIT",
  OVER_QUERY_LIMIT = "OVER_QUERY_LIMIT",
  REQUEST_DENIED = "REQUEST_DENIED",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  ZERO_RESULTS = "ZERO_RESULTS",
  NOT_FOUND = "NOT_FOUND",
}

interface ResponseData {
  status: Status
  error_message: string
  html_attributions?: string[]
  next_page_token?: string
}

export interface LatLngLiteral {
  lat: number
  lng: number
}

export interface GeocodeResult {
  types: string[]
  formatted_address: string
  address_components: {
    types: string[]
    long_name: string
    short_name: string
  }[]
  postcode_localities: string[]
  geometry: {
    location: LatLngLiteral
    location_type?: string
    viewport: {
      northeast: LatLngLiteral
      southwest: LatLngLiteral
    }
    bounds?: {
      northeast: LatLngLiteral
      southwest: LatLngLiteral
    }
  }
  plus_code: {
    global_code: string
    compound_code: string
  }
  partial_match: boolean
  place_id: string
}

interface GeocodeResponseData extends ResponseData {
  results: GeocodeResult[]
}

export interface GeocodeResponse extends AxiosResponse {
  data: GeocodeResponseData
}

export async function getGeoCodes(inputValue: string) {
  try {
    const address = encodeURIComponent(inputValue)
    const response = await axios.get(`${urlPath}/geocode/${address}`, {})
    return response.data as GeocodeResponse
  } catch (err) {
    console.error(err)
  }
}

export async function getGeoCodesByPlaceId(inputValue: string) {
  try {
    const placeId = inputValue.trim()
    const response = await axios.get(`${urlPath}/geocodeById/${placeId}`)
    return response.data as GeocodeResponse
  } catch (err) {
    console.error(err)
    captureException(
      new Error(`Error fetching geocodes by place Id ${JSON.stringify(err)}`),
    )
  }
}
